import { useOktaAuth } from "@okta/okta-react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AUTH_BYPASS } from "@/auth-bypass";
import { useLogoutLoading } from "@/contexts/logout-loading-context";

interface ProtectedRouteProps {
	children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
	const { authState } = useOktaAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { isLoggingOut } = useLogoutLoading();

	useEffect(() => {
		if (AUTH_BYPASS || isLoggingOut) {
			return;
		}

		if (authState && !authState.isAuthenticated) {
			const redirectUrl = location.pathname + location.search;
			navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`, {
				replace: true,
			});
		}
	}, [authState, location.pathname, location.search, navigate, isLoggingOut]);

	if (AUTH_BYPASS) {
		return <>{children}</>;
	}

	if (authState === null) {
		return <LoadingState />;
	}

	if (isLoggingOut) {
		return null;
	}

	if (!authState?.isAuthenticated) {
		return <LoadingState />;
	}

	return <>{children}</>;
};

const LoadingState = () => {
	return (
		<div className="flex min-h-screen items-center justify-center">
			<div className="text-center">
				<div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-current border-r-transparent border-solid align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]" />
				<p className="text-gray-600 text-lg">Verifying authentication...</p>
			</div>
		</div>
	);
};
